/**
 * A web page an AI analysis cites (specs/systemic-v2-analysis-depth.md → C).
 *
 * Kept apart from the shared SourceChip on purpose: a RAG chip opens a chunk of
 * the vetted corpus, this one opens a page on the web, and the two must not be
 * mistaken for each other. Teal and a globe (RAG is purple and a book), the
 * publisher's credibility as the server's domain list labelled it (research,
 * official, organisation, interest group or news), and the
 * domain. Clicking shows what the page was cited for — the text quoted from it,
 * its date and the question it answered — with a link that opens it.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Globe, ExternalLink } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

// Credibility shades: evidence (research, official) solid, context (organisation, interest, news) muted.
const CRED = {
  research: { color: '#5eead4', bg: 'rgba(45, 212, 191, 0.16)' },
  official: { color: '#7dd3fc', bg: 'rgba(56, 189, 248, 0.16)' },
  organisation: { color: '#cbd5e1', bg: 'rgba(203, 213, 225, 0.12)' },
  // A party to the question: its own colour, so a position never passes for a finding (#1171).
  interest: { color: '#fdba74', bg: 'rgba(253, 186, 116, 0.14)' },
  news: { color: '#fcd34d', bg: 'rgba(252, 211, 77, 0.12)' },
};

const Wrap = styled.span`display: inline-flex; flex-direction: column; max-width: 100%;`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 340px;
  padding: 3px 10px 3px 8px;
  border-radius: 6px;
  border: 1px solid rgba(45, 212, 191, 0.3);
  background: rgba(45, 212, 191, 0.07);
  color: #99f6e4;
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  &:hover { background: rgba(45, 212, 191, 0.14); border-color: rgba(45, 212, 191, 0.5); }
  &:focus-visible { outline: 2px solid #5eead4; outline-offset: 2px; }
  svg { flex-shrink: 0; }
`;

const Num = styled.span`font-weight: 700; font-size: 11px; color: #5eead4; flex-shrink: 0;`;
const Title = styled.span`overflow: hidden; text-overflow: ellipsis;`;
const Domain = styled.span`color: rgba(153, 246, 228, 0.65); flex-shrink: 0;`;

const Badge = styled.span`
  flex-shrink: 0;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: ${(p) => p.$c.color};
  background: ${(p) => p.$c.bg};
`;

const Detail = styled.div`
  margin-top: 6px;
  padding: 8px 10px;
  max-width: 520px;
  border-left: 2px solid rgba(45, 212, 191, 0.5);
  background: rgba(45, 212, 191, 0.05);
  font-size: 12px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  white-space: normal;
`;

const Quote = styled.blockquote`margin: 0 0 6px; font-style: italic;`;
const Meta = styled.div`color: rgba(255, 255, 255, 0.55); margin-top: 4px;`;
const Link = styled.a`
  display: inline-flex; align-items: center; gap: 4px; margin-top: 6px;
  color: #5eead4; font-weight: 600; text-decoration: none;
  &:hover { text-decoration: underline; }
`;

export default function WebSourceChip({ source, number }) {
  const { t, language } = useTranslation();
  const [open, setOpen] = useState(false);
  const c = CRED[source.credibility] || CRED.organisation;
  const label = t(`systemicMapV2.sources.credibility.${source.credibility}`);
  return (
    <Wrap>
      <Chip
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={`${source.title} — ${source.host || source.domain}`}
      >
        <Globe size={12} aria-hidden="true" />
        {number ? <Num>{number}</Num> : null}
        <Badge $c={c}>{label}</Badge>
        <Title>{source.title}</Title>
        <Domain>{source.domain}</Domain>
      </Chip>
      {open ? (
        <Detail>
          {source.excerpt ? <Quote>“{source.excerpt}”</Quote> : null}
          {source.questionTexts?.length ? (
            <Meta>{t('systemicMapV2.sources.answered')}: {source.questionTexts.join(' · ')}</Meta>
          ) : null}
          {source.pageDate ? (
            <Meta>{t('systemicMapV2.sources.pageAge')}: {new Date(source.pageDate).toLocaleDateString(language)}</Meta>
          ) : null}
          <Link href={source.url} target="_blank" rel="noopener noreferrer">
            {t('systemicMapV2.sources.openPage')} <ExternalLink size={12} aria-hidden="true" />
          </Link>
        </Detail>
      ) : null}
    </Wrap>
  );
}
